import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { ComplianceChip } from "../components/ComplianceChip";
import { AllergyBanner } from "../components/AllergyBanner";
import { ClientOnboarding } from "../components/ClientOnboarding";
import { ClientMedical } from "./ClientMedical";
import { Card, Btn, Empty, Modal, Badge } from "../components/UI";
import { NativeLikeSelect } from "../components/CocoaSelect";
import { FLabel, FInput, FSelect, SelectWithCustom } from "./engagement/FormControls";
import { usePrograms } from "../lib/usePrograms";
import { FaceSheet } from "./FaceSheet";
import { AvatarWithStatus } from "../components/StatusDot";
import { ClientProfile } from "./ClientProfile";
import { AllergiesPanel } from "./AllergiesPanel";
import { AimsPanel } from "./AimsPanel";
import { FamilyPanel } from "./FamilyPanel";
import { AuthorizationsPanel } from "./AuthorizationsPanel";
import { AdmissionsPanel, DiagnosisPanel, MedicationsPanel, CompliancePanel } from "./ClinicalTabPanels";
import { PharmacyPanel, ImmunizationsPanel, ComPanel } from "./InfoExtraPanels";
import { CommunicationLog } from "../components/comms/CommunicationLog";
import { ClientFormsPanel } from "../components/ClientFormsPanel";
import { VitalsPanel, AssessmentsChartPanel, TreatmentPlansChartPanel, ProgressNotesChartPanel, GroupNotesPanel, BehaviorChartPanel, CollateralPanel, DocumentsPanel, AsamChartPanel, IntakeChartPanel } from "./ChartPanels";
import { MessagesPanel } from "./MessagesPanel";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { SignaturePromptModal } from "../components/SignaturePromptModal";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { DateField } from "./engagement/DateField";
import { Plus, Search, ChevronDown, AlertTriangle, Eye, Pencil, Trash2, MapPin, Filter, Check, PanelLeftClose, PanelLeftOpen, SquareArrowOutUpRight, HeartHandshake, Copy, Mail, KeyRound, ShieldCheck, ShieldOff, UsersRound, Zap } from "lucide-react";
import { ClientQuickDrawer } from "../components/ClientQuickDrawer";
import { toast } from "sonner";

// Grouped structure for the client detail top strip. Two tiers: group buttons on top, then the group's tabs.
// Every intake section shows up under Intake so we do not lose parity with the intake wizard.
export const INFO_TAB_GROUPS = [
  { key: "personal", label: "Personal", tabs: ["Client Information", "Face Sheet", "Client Profile", "Family Information", "Emergency Information"] },
  { key: "intake", label: "Intake", tabs: ["Demographics", "Referral Source", "Insurance", "Requested Services", "Presenting Problem", "Housing", "Employer / School", "External Entities", "Permission to Communicate", "Call Portal", "Authorized Programs"] },
  { key: "chart", label: "Chart", tabs: ["Medical History", "Diagnosis", "Medications", "Pharmacy", "Allergies", "Immunizations"] },
  { key: "clinical", label: "Clinical", tabs: ["Compliance Tracking", "COM"] },
  { key: "admin", label: "Admin", tabs: ["Admission & Discharge", "Authorizations", "Tasks", "Messages", "Communications"] },
];

// Flat list kept for downstream imports (ClientConsole.js filter uses this).
export const INFO_TABS = INFO_TAB_GROUPS.flatMap((g) => g.tabs);
const CHART_TABS = ["Complete Chart", "Intake", "Orders", "Clinical Assessments", "Treatment Plans", "Crisis/Safety Plan", "Medical", "AIMS", "Vitals", "ASAM", "Progress Notes", "Group Notes", "TTM", "Behavior Tracking", "Transition/Discharge", "Collateral Contacts", "Scanned Documents", "Legacy Documents"];
const GENERIC_CHART_TABS = ["Complete Chart", "Orders", "Crisis/Safety Plan", "Medical", "TTM", "Transition/Discharge"];
const ALERT_COLORS = { red: "bg-[#D32F2F]", blue: "bg-[#3D5AFE]", purple: "bg-[#7C4DFF]" };
const NOTE_EMPTY = { date: dayjs().format("YYYY-MM-DD"), time: "09:00", note_type: "", provider: "", program: "", category: "Progress Notes", custom_notes: "" };

// Built-in note types (per Flynaut spec 3c). Admins can extend via Clinical Forms Library (category = "Chart Note").
const BUILTIN_NOTE_TYPES = [
  "Progress Note", "Nursing Note", "Nursing Assessment", "Medication Note",
  "Group Note", "Discharge Note", "Intake Note", "Crisis Note", "Custom",
];

function Avatar({ c, size = "h-9 w-9 text-xs" }) {
  const hues = ["bg-[#8D6E63]", "bg-[#A1887F]", "bg-[#6D4C41]", "bg-[#4E342E]", "bg-[#795548]"];
  const i = (c.first_name?.charCodeAt(0) || 0) % hues.length;
  const rounded = size.includes("rounded") ? "" : "rounded-full";
  if (c.photo) return <img src={c.photo} alt={`${c.first_name} ${c.last_name}`} className={`${size} ${rounded} object-cover shrink-0`} />;
  return <div className={`${size} ${hues[i]} ${rounded} text-white flex items-center justify-center font-bold shrink-0`}>{c.first_name?.[0]}{c.last_name?.[0]}</div>;
}

// ---------- Intake section preview for client detail sub-tabs ----------
// Each key maps a client-detail tab label to the intake wizard step slug.
// The InfoPanel renders a compact summary of the captured fields, plus an
// "Edit in Intake" link that opens the full intake wizard for that step.
const INTAKE_TAB_HANDLERS = {
  "Demographics": { step: "demographics", path: "demographics" },
  "Referral Source": { step: "referral_source", path: "referral_source" },
  "Insurance": { step: "insurance", path: "insurance" },
  "Requested Services": { step: "requested_services", path: "requested_services" },
  "Presenting Problem": { step: "presenting_problem", path: "presenting_problem" },
  "Housing": { step: "housing", path: "housing" },
  "Employer / School": { step: "employer_school", path: "employer_school" },
  "External Entities": { step: "external_entities", path: "external_entities" },
  "Permission to Communicate": { step: "permission_to_communicate", path: "permission_to_communicate" },
  "Call Portal": { step: "call_portal", path: "call_portal" },
  "Authorized Programs": { step: "authorized_programs", path: "authorized_programs" },
};

function IntakeSectionView({ client, intake, tab }) {
  const cfg = INTAKE_TAB_HANDLERS[tab];
  const section = intake ? intake[cfg.step] : null;
  const editHref = intake?.id
    ? `/engagement/intake/${intake.id}/edit?step=${cfg.step}`
    : `/engagement/intake/new?client_id=${client.id}&step=${cfg.step}`;
  return (
    <div className="space-y-3" data-testid={`intake-section-view-${cfg.step}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-heading text-sm font-bold text-[#3E2723]">{tab}</div>
          <div className="text-[11px] text-[#7B6157]">Captured during intake. Any edits sync back to the intake record.</div>
        </div>
        <Link data-testid={`intake-edit-${cfg.step}`} to={editHref}
          className="rounded-full bg-[#4E342E] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#5D4037]">{intake?.id ? "Edit in Intake" : "Start Intake"}</Link>
      </div>
      {!section || Object.keys(section).length === 0 ? (
        <Empty text={`No ${tab.toLowerCase()} captured yet. Click "Edit in Intake" to add.`} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 rounded-2xl bg-[#FDFCF8] border border-[#EFEBE0] p-4">
          {Object.entries(section).map(([k, v]) => (
            <IntakeFieldPreview key={k} field={k} value={v} />
          ))}
        </div>
      )}
    </div>
  );
}

function IntakeFieldPreview({ field, value }) {
  const label = field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  let display;
  if (value == null || value === "") display = "-";
  else if (typeof value === "boolean") display = value ? "Yes" : "No";
  else if (Array.isArray(value)) display = value.length === 0 ? "-" : value.join(", ");
  else if (typeof value === "object") {
    const kids = Object.entries(value).filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0));
    display = kids.length === 0 ? "-" : (
      <div className="space-y-0.5">
        {kids.map(([k, v]) => (<div key={k} className="text-[13px]"><span className="text-[#7B6157]">{k.replace(/_/g, " ")}: </span><span className="text-[#3E2723]">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>))}
      </div>
    );
  } else display = String(value);
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[#7B6157]">{label}</div>
      <div className="mt-0.5 text-sm text-[#3E2723] break-words">{display}</div>
    </div>
  );
}

function TabStrip({ tabs, active, onChange, testPrefix, maxVisible = 8, groups, mode = "compact" }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState(() => {
    if (!groups) return null;
    const found = groups.find((g) => g.tabs.includes(active));
    return (found || groups[0]).key;
  });
  // If the caller flips mode from compact to expand externally, keep the
  // group selection in sync with wherever the active tab lives.
  useEffect(() => {
    if (!groups) return;
    const found = groups.find((g) => g.tabs.includes(active));
    if (found && found.key !== activeGroup) setActiveGroup(found.key);
  }, [active, groups, activeGroup]);
  const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  // Grouped/compact mode: two tiers - group buttons on top, tabs of the active
  // group below. Used by default so clinicians see a curated shelf of related
  // tabs rather than a wall of options.
  if (groups && mode === "compact") {
    const current = groups.find((g) => g.key === activeGroup) || groups[0];
    const visible = current.tabs.slice(0, maxVisible);
    const overflow = current.tabs.slice(maxVisible);
    const activeInOverflow = overflow.includes(active);
    return (
      <div className="relative mb-4 space-y-2">
        <div className="flex items-center gap-1 rounded-lg bg-[#F1EEE7] p-1" data-testid={`${testPrefix}-groups`}>
          {groups.map((g) => {
            const isActive = g.key === activeGroup;
            return (
              <button key={g.key} type="button" data-testid={`${testPrefix}-group-${g.key}`}
                onClick={() => { setActiveGroup(g.key); onChange(g.tabs[0]); setMoreOpen(false); }}
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${isActive ? "bg-[#4E342E] text-white shadow-sm" : "text-[#7B6157] hover:text-[#4E342E]"}`}>
                {g.label}
              </button>
            );
          })}
        </div>
        <div className="relative">
          <div className="flex items-center rounded-lg bg-[#FDFCF8] border border-[#EFEBE0] overflow-x-auto no-scrollbar">
            {visible.map((t) => (
              <button key={t} type="button" data-testid={`${testPrefix}-${slug(t)}`} onClick={() => onChange(t)}
                className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${t === active ? "bg-[#D9C7B2] text-[#3E2723]" : "text-[#84675B] hover:text-[#6D4C41]"}`}>{t}</button>
            ))}
            {overflow.length > 0 && (
              <button type="button" data-testid={`${testPrefix}-more`} onClick={() => setMoreOpen((o) => !o)}
                className={`ml-auto flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${activeInOverflow ? "bg-[#D9C7B2] text-[#3E2723]" : "text-[#7B6157] hover:text-[#4E342E]"}`}>
                {activeInOverflow ? active : "More"} <ChevronDown size={14} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
          {moreOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setMoreOpen(false)} />
              <div className="absolute right-0 top-full z-30 mt-1 w-64 rounded-xl border border-[#EFEBE0] bg-white py-1.5 shadow-xl">
                {overflow.map((t) => (
                  <button key={t} type="button" data-testid={`${testPrefix}-${slug(t)}`} onClick={() => { onChange(t); setMoreOpen(false); }}
                    className={`block w-full px-4 py-2.5 text-left text-sm transition-colors ${t === active ? "bg-[#F5F2EB] font-semibold text-[#3E2723]" : "text-[#6D4C41] hover:bg-[#FDFCF8]"}`}>{t}</button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
  // Expand mode: flatten every group's tabs into one horizontal strip. Users
  // who prefer a single-tier layout can toggle here via the parent header
  // (`workspace-tabmode-toggle`). Overflow past 12 collapses into a "More"
  // dropdown, matching the legacy flat-mode caller behaviour.
  const flatTabs = groups ? groups.flatMap((g) => g.tabs) : tabs;
  const flatMax = groups ? 12 : maxVisible;
  const visible = flatTabs.slice(0, flatMax);
  const overflow = flatTabs.slice(flatMax);
  const activeInOverflow = overflow.includes(active);
  return (
    <div className="relative mb-4">
      <div className="flex items-center rounded-lg bg-[#F1EEE7] overflow-x-auto no-scrollbar">
        {visible.map((t) => (
          <button key={t} type="button" data-testid={`${testPrefix}-${slug(t)}`} onClick={() => onChange(t)}
            className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${t === active ? "bg-[#D9C7B2] text-[#3E2723]" : "text-[#84675B] hover:text-[#6D4C41]"}`}>{t}</button>
        ))}
        {overflow.length > 0 && (
          <button type="button" data-testid={`${testPrefix}-more`} onClick={() => setMoreOpen((o) => !o)}
            className={`ml-auto flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${activeInOverflow ? "bg-[#D9C7B2] text-[#3E2723]" : "text-[#7B6157] hover:text-[#4E342E]"}`}>
            {activeInOverflow ? active : "More"} <ChevronDown size={14} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {moreOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMoreOpen(false)} />
          <div className="absolute right-0 top-full z-30 mt-1 w-64 rounded-xl border border-[#EFEBE0] bg-white py-1.5 shadow-xl">
            {overflow.map((t) => (
              <button key={t} type="button" data-testid={`${testPrefix}-${slug(t)}`} onClick={() => { onChange(t); setMoreOpen(false); }}
                className={`block w-full px-4 py-2.5 text-left text-sm transition-colors ${t === active ? "bg-[#F5F2EB] font-semibold text-[#3E2723]" : "text-[#6D4C41] hover:bg-[#FDFCF8]"}`}>{t}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div className="min-w-0">
    <div className="font-heading text-[15px] font-bold text-[#3E2723]">{label}</div>
    <div className="mt-0.5 text-sm text-[#6D4C41] break-words">{value || "-"}</div>
  </div>
);

export function InfoPanel({ tab, client, intake, authorizations, tasks, onUpdated, onOpenMedList }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  if (tab === "Client Information") {
    const age = client.dob ? dayjs().diff(dayjs(client.dob), "year") : "-";
    return (
      <div className="flex flex-wrap gap-6">
        <Avatar c={client} size="h-24 w-24 text-2xl rounded-2xl" />
        <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-7 gap-x-6 gap-y-5">
          <Field label="First Name" value={client.first_name} />
          <Field label="Middle Name" value={intake?.demographics?.middle_name} />
          <Field label="Last Name" value={client.last_name} />
          <Field label="Birth Date" value={client.dob} />
          <Field label="Age" value={age} />
          <Field label="Health Record Number" value={client.health_record_number} />
          <Field label="SSN" value={intake?.demographics?.ssn || "-"} />
          <Field label="Street Address" value={client.address} />
          <Field label="City" value={client.city} />
          <Field label="State" value={client.state} />
          <Field label="Zip Code" value={client.zip} />
          <Field label="County" value={intake?.demographics?.county} />
          <Field label="Contact No" value={client.phone} />
          <Field label="Email Address" value={client.email} />
          <div className="col-span-2">
            <button type="button" className="flex items-center gap-1 text-sm font-semibold text-[#7B6157] underline underline-offset-4"><MapPin size={14} /> Location</button>
          </div>
        </div>
      </div>
    );
  }
  if (tab === "Emergency Information") {
    return <FamilyPanel client={client} emergencyMode />;
  }
  if (tab === "Family Information") {
    return <FamilyPanel client={client} />;
  }
  if (tab === "Authorizations") {
    return <AuthorizationsPanel client={client} />;
  }
  if (tab === "Diagnosis") {
    return <DiagnosisPanel client={client} onUpdated={onUpdated} />;
  }
  if (tab === "Medications") {
    return <MedicationsPanel client={client} onUpdated={onUpdated} onOpenMasterList={onOpenMedList} />;
  }
  if (tab === "Allergies") {
    return <AllergiesPanel client={client} onUpdated={onUpdated} />;
  }
  if (tab === "Tasks") {
    return tasks.length === 0 ? <Empty text="No tasks linked to this client" /> : (
      <div className="space-y-2">
        {tasks.map((t) => (
          <Link key={t.id} to={`/tasks/${t.id}`}
            state={{ from: { url: `/clients/${client.id}`, label: `${client.first_name} ${client.last_name}` } }}
            data-testid={`client-task-link-${t.id}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#FDFCF8] border border-[#EFEBE0] px-4 py-3 hover:bg-[#F5F2EB] transition-colors">
            <span className="text-sm font-medium text-[#3E2723]">{t.title}</span>
            <span className="flex items-center gap-2 text-xs text-[#7B6157]">Due {t.due_date || "-"} <Badge value={t.priority} /><Badge value={t.status} /></span>
          </Link>
        ))}
      </div>
    );
  }
  if (tab === "Communications") {
    return <CommunicationLog client={client} editable={editable} />;
  }
  if (tab === "Messages") {
    return <MessagesPanel client={client} editable={editable} />;
  }
  if (tab === "Admission & Discharge") {
    return <AdmissionsPanel client={client} onUpdated={onUpdated} />;
  }
  if (tab === "Compliance Tracking") {
    return <CompliancePanel client={client} />;
  }
  if (tab === "Pharmacy") {
    return <PharmacyPanel client={client} editable={editable} />;
  }
  if (tab === "Immunizations") {
    return <ImmunizationsPanel client={client} editable={editable} />;
  }
  if (tab === "COM") {
    return <ComPanel client={client} />;
  }
  if (tab === "Onboarding") {
    return <ClientOnboarding clientId={client.id} />;
  }
  if (tab === "Medical History") {
    const chars = Object.entries(intake?.diagnosis?.characteristics || {}).filter(([, v]) => v === "yes").map(([k]) => k);
    return (
      <div className="space-y-4">
        <div className="rounded-2xl bg-[#F5F2EB] p-4">
          <div className="font-heading text-sm font-bold text-[#3E2723] mb-1">Presenting Problem</div>
          <p className="text-sm text-[#6D4C41]">{intake?.presenting_problem?.summary || "No history recorded yet."}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
          <Field label="Primary Care M.D." value={intake?.diagnosis?.primary_care_md} />
          <Field label="PCP Phone" value={intake?.diagnosis?.phone} />
          <Field label="Primary Diagnosis" value={client.diagnosis} />
        </div>
        {chars.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {chars.map((c) => <span key={c} className="rounded-full bg-[#EDE7F6] text-[#5E35B1] px-3 py-1 text-xs font-semibold">{c}</span>)}
          </div>
        )}
      </div>
    );
  }
  if (tab === "Face Sheet") {
    return <FaceSheet client={client} intake={intake} authorizations={authorizations} />;
  }
  if (tab === "Client Profile") {
    return <ClientProfile client={client} onUpdated={onUpdated} />;
  }
  // ---------- Intake sub-tabs (read-only summaries; edit via Intake wizard) ----------
  if (INTAKE_TAB_HANDLERS[tab]) {
    return <IntakeSectionView client={client} intake={intake} tab={tab} />;
  }
  return <Empty text={`${tab} module is coming in a later phase`} />;
}

export default function ClientsWorkspace() {
  const { user } = useAuth();
  const PROGRAMS = usePrograms();
  const clinicalEditable = canEdit(user, "clinical");
  const { id } = useParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(id || null);
  const [infoTab, setInfoTab] = useState("Client Information");
  const [chartTab, setChartTab] = useState("Complete Chart");
  const [medListKey, setMedListKey] = useState(0);
  useEffect(() => { setMedListKey(0); }, [selectedId]);
  const openMasterMedList = () => {
    setMedListKey((k) => k + 1);
    setChartTab("Medical");
    setTimeout(() => document.querySelector('[data-testid="client-medical-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };
  const [tabMode, setTabMode] = useState(() => {
    try { return localStorage.getItem("cocoa_workspace_tab_mode") || "expand"; }
    catch { return "expand"; }
  });
  const toggleTabMode = () => {
    setTabMode((m) => {
      const next = m === "compact" ? "expand" : "compact";
      try { localStorage.setItem("cocoa_workspace_tab_mode", next); } catch {
        // localStorage may be blocked (private browsing); the toggle still
        // works for the current session.
      }
      return next;
    });
  };
  const [notes, setNotes] = useState([]);
  const [signedFilter, setSignedFilter] = useState("");
  const [intake, setIntake] = useState(null);
  const [authorizations, setAuthorizations] = useState([]);
  const [compliance, setCompliance] = useState({});
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [portalStatus, setPortalStatus] = useState(null);
  const [portalManage, setPortalManage] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const loadPortalStatus = (cid) => api.get(`/clients/${cid}/portal-status`).then((r) => setPortalStatus(r.data)).catch(() => setPortalStatus(null));

  const invitePortal = async (c) => {
    setInviting(true);
    try {
      const r = await api.post(`/clients/${c.id}/portal-invite`, {});
      setInviteResult({ ...r.data, client_name: `${c.first_name} ${c.last_name}` });
      setPortalManage(false);
      loadPortalStatus(c.id);
    } catch (e) { toast.error(apiError(e)); }
    setInviting(false);
  };

  const togglePortalAccess = async (c, disable) => {
    setPortalBusy(true);
    try {
      await api.put(`/clients/${c.id}/portal-access`, { disable });
      toast.success(disable ? "Portal access deactivated" : "Portal access reactivated");
      loadPortalStatus(c.id);
    } catch (e) { toast.error(apiError(e)); }
    setPortalBusy(false);
  };

  const emailClientInvite = (email, tempPassword) => {
    const subject = encodeURIComponent("Your secure client portal access");
    const body = encodeURIComponent(
      `Hello,\n\nYour secure client portal is ready.\n\n1. Go to: ${window.location.origin}/login\n2. Choose the "Client Portal" tab\n3. Sign in with: ${email}\n${tempPassword ? `4. Temporary password: ${tempPassword}\n` : ""}\nOn your first sign-in you will set a new password and enable two-factor authentication.\n\nFor your privacy, please do not reply to this email with personal health information.\n\n- Your care team`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };
  const [noteModal, setNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState(NOTE_EMPTY);
  const [editNoteId, setEditNoteId] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [signingNote, setSigningNote] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [clientTasks, setClientTasks] = useState([]);
  const [priorityRail, setPriorityRail] = useState(null);
  const [quickClient, setQuickClient] = useState(null);
  const [customNoteTypes, setCustomNoteTypes] = useState([]);

  useEffect(() => {
    api.get("/clients/priority", { params: { limit: 20 } }).then((r) => setPriorityRail(r.data)).catch(() => {});
    // Load admin-defined note types so the Add Note dropdown can merge them with the built-ins.
    api.get("/note-types").then((r) => setCustomNoteTypes(r.data)).catch(() => setCustomNoteTypes([]));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    api.get("/tasks").then((r) => setClientTasks(r.data.filter((t) => t.client_id === selectedId))).catch(() => setClientTasks([]));
  }, [selectedId]);

  useEffect(() => {
    api.get("/clients", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => {
      setClients(r.data);
      if (!selectedId && r.data.length) setSelectedId(id || r.data[0].id);
    });
  }, [statusFilter]);
  useEffect(() => { api.get("/dashboard").then((r) => setAuthorizations(r.data.authorizations)).catch(() => {}); }, []);
  useEffect(() => { api.get("/clients/compliance-scores").then((r) => setCompliance(r.data)).catch(() => {}); }, []);

  const client = useMemo(() => clients.find((c) => c.id === selectedId), [clients, selectedId]);
  useEffect(() => { setPortalStatus(null); if (selectedId) loadPortalStatus(selectedId); }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps
  const isAdmin = user?.role === "admin" || user?.is_super_admin;

  const loadNotes = () => {
    if (!selectedId) return;
    if (!GENERIC_CHART_TABS.includes(chartTab)) return;
    const params = { category: chartTab };
    if (signedFilter) params.signed = signedFilter;
    api.get(`/clients/${selectedId}/chart-notes`, { params }).then((r) => setNotes(r.data));
  };
  useEffect(() => { loadNotes(); }, [selectedId, chartTab, signedFilter]);
  useEffect(() => {
    if (!selectedId) return;
    setIntake(null);
    api.get("/intake-forms", { params: { limit: 100 } }).then((r) => {
      setIntake(r.data.items.find((f) => f.client_id === selectedId) || null);
    });
  }, [selectedId]);

  const filtered = clients.filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase()));

  const toggleSign = async (n) => {
    // Open the mandatory signature prompt instead of silently PUTing signed:true.
    // This ensures every sign action has a signature image, a name, and produces
    // an audit stamp (signature_id, doc_hash, IP, timestamp) on the backend.
    setSigningNote(n);
  };

  const applySignature = async ({ image, name }) => {
    const n = signingNote;
    if (!n) return;
    try {
      const payload = { signed: true, signature_name: name || "", signature_image: image || "" };
      await api.put(`/chart-notes/${n.id}`, payload);
      setNotes((ns) => ns.map((x) => (x.id === n.id ? { ...x, signed: true } : x)));
      setViewNote((v) => (v && v.id === n.id ? { ...v, signed: true } : v));
      toast.success("Note signed and locked with audit stamp");
      setSigningNote(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveNote = async (e) => {
    e.preventDefault();
    try {
      if (editNoteId) await api.put(`/chart-notes/${editNoteId}`, noteForm);
      else await api.post("/chart-notes", { ...noteForm, client_id: selectedId, category: chartTab === "Complete Chart" ? noteForm.category : chartTab });
      toast.success(editNoteId ? "Note updated" : "Note added");
      setNoteModal(false); loadNotes();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doDelete = async () => {
    await api.delete(`/chart-notes/${confirmDelete.id}`);
    toast.success("Note deleted");
    setConfirmDelete(null); loadNotes();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-5 items-start">
      {!panelOpen ? (
        <div className="w-full lg:w-14 shrink-0 lg:sticky lg:top-[100px]">
          <button type="button" data-testid="expand-client-panel" aria-label="Expand client list" onClick={() => setPanelOpen(true)}
            className="flex h-11 w-full lg:w-11 items-center justify-center rounded-xl bg-white border border-[#EFEBE0] text-[#6D4C41] hover:bg-[#F5F2EB] shadow-sm">
            <PanelLeftOpen size={18} />
          </button>
          <div className="hidden lg:flex flex-col items-center gap-2.5 mt-2 p-1.5 max-h-[calc(100vh-270px)] overflow-y-auto">
            {(priorityRail?.clients || filtered.slice(0, 20)).map((c) => {
              const ring = c.id === selectedId ? "ring-[#2E7D32]" : c.priority === "high" ? "ring-[#D32F2F]" : c.priority === "medium" ? "ring-[#E65100]" : "ring-transparent hover:ring-[#A5D6A7]";
              const tip = `${c.first_name} ${c.last_name}${(c.reasons || []).length ? `\nNeeds attention:\n• ${c.reasons.join("\n• ")}` : ""}`;
              return (
                <button key={c.id} type="button" data-testid={`rail-client-${c.id}`} title={tip}
                  onClick={() => { setSelectedId(c.id); navigate(`/clients/${c.id}`, { replace: true }); }}
                  className={`h-9 w-9 shrink-0 rounded-full ring-2 ring-offset-2 ring-offset-[#FDFCF8] transition-all focus:outline-none ${ring}`}>
                  <AvatarWithStatus status={c.status}><Avatar c={c} /></AvatarWithStatus>
                </button>
              );
            })}
          </div>
          <button type="button" data-testid="rail-complete-list-btn" title={`Complete client list${priorityRail ? ` (${priorityRail.total} clients)` : ""}`}
            onClick={() => navigate("/clients/all")}
            className="mt-2 hidden lg:flex h-11 w-full lg:w-11 items-center justify-center rounded-xl bg-[#4E342E] text-white hover:bg-[#3E2723] shadow-sm mx-auto">
            <UsersRound size={17} />
          </button>
        </div>
      ) : (
      <Card className="w-full lg:w-[380px] shrink-0 lg:sticky lg:top-[100px]" data-testid="client-list-panel">
        <div className="flex items-center justify-between gap-2 p-4 pb-2">
          <FSelect data-testid="client-status-filter" placeholder="All Clients" className="w-40 h-10" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="active">Active Clients</option><option value="unprocessed">Unprocessed Clients</option>
            <option value="referred">Pending (Pre-Admit) Clients</option><option value="waitlist">Waitlist Clients</option>
            <option value="denied">Denied Clients</option><option value="inactive">Inactive/Alumni Clients</option>
            <option value="discharged">Discharged</option>
          </FSelect>
          <div className="flex items-center gap-1.5">
            <Btn data-testid="workspace-add-client-btn" className="rounded-lg px-3 py-2 text-xs whitespace-nowrap shrink-0" onClick={() => navigate("/engagement/intake/new")}><Plus size={14} /> Add New Client</Btn>
            <button type="button" data-testid="collapse-client-panel" aria-label="Collapse client list" onClick={() => setPanelOpen(false)}
              className="h-9 w-9 shrink-0 rounded-lg border border-[#EFEBE0] text-[#6D4C41] flex items-center justify-center hover:bg-[#F5F2EB]">
              <PanelLeftClose size={16} />
            </button>
          </div>
        </div>
        <div className="relative px-4 pb-3">
          <Search size={15} className="absolute left-7 top-3 text-[#84675B]" />
          <FInput data-testid="client-list-search" placeholder="Search clients…" className="pl-9 h-10" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-4 py-2 bg-[#EDE7DC] text-xs font-semibold text-[#4E342E]">
          <span>Name</span><span>Record #</span><span>Phone</span>
        </div>
        <div className="max-h-72 lg:max-h-[calc(100vh-320px)] overflow-y-auto">
          {filtered.map((c) => (
            <button key={c.id} type="button" data-testid={`client-item-${c.id}`} onClick={() => { setSelectedId(c.id); navigate(`/clients/${c.id}`, { replace: true }); }}
              className={`group grid w-full grid-cols-[1fr_auto_auto] items-center gap-2 px-4 py-2.5 text-left border-b border-[#F5F2EB] transition-colors ${c.id === selectedId ? "bg-[#F5F2EB]" : "hover:bg-[#FDFCF8]"}`}>
              <span className="flex items-center gap-2 min-w-0">
                <AvatarWithStatus status={c.status}><Avatar c={c} /></AvatarWithStatus>
                <span className="truncate text-sm font-medium text-[#3E2723]">{c.first_name} {c.last_name}</span>
                {(c.alerts || []).length > 0 && <AlertTriangle size={13} className="text-[#D32F2F] shrink-0" />}
                <ComplianceChip data={compliance[c.id]} testId={`compliance-chip-${c.id}`} />
                <span role="button" tabIndex={0} data-testid={`open-console-${c.id}`} title="Open chart console in new tab"
                  onClick={(e) => { e.stopPropagation(); window.open(`/clients/${c.id}/console`, "_blank"); }}
                  className="ml-auto mr-1 hidden group-hover:flex group-focus-within:flex h-6 w-6 items-center justify-center rounded-md bg-[#EDE7DC] text-[#6D4C41] hover:bg-[#4E342E] hover:text-white transition-colors shrink-0">
                  <SquareArrowOutUpRight size={12} />
                </span>
                <span role="button" tabIndex={0} data-testid={`quick-actions-${c.id}`} title="Check in / discharge"
                  onClick={(e) => { e.stopPropagation(); setQuickClient(c); }}
                  className="mr-1 hidden group-hover:flex group-focus-within:flex h-6 w-6 items-center justify-center rounded-md bg-[#EDE7DC] text-[#6D4C41] hover:bg-[#2E7D32] hover:text-white transition-colors shrink-0">
                  <Zap size={12} />
                </span>
              </span>
              <span className="text-[11px] text-[#6D4C41] tabular-nums">{c.health_record_number || c.mrn}</span>
              <span className="text-[11px] text-[#6D4C41] tabular-nums whitespace-nowrap">{(c.phone || "").slice(0, 14)}</span>
            </button>
          ))}
          {filtered.length === 0 && <Empty text="No clients found" />}
        </div>
        <button type="button" data-testid="panel-complete-list-btn" onClick={() => navigate("/clients/all")}
          className="flex w-full items-center justify-center gap-1.5 border-t border-[#F1EEE7] px-4 py-2.5 text-xs font-bold text-[#6D4C41] hover:bg-[#F5F2EB] rounded-b-2xl">
          <UsersRound size={13} /> Complete client list →
        </button>
      </Card>
      )}

      <div className="w-full flex-1 min-w-0">
        {!client ? <Empty text="Select a client" /> : (
          <>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate font-heading text-sm font-bold text-[#3E2723]">{client.first_name} {client.last_name}</span>
                <ComplianceChip data={compliance[client.id]} testId="workspace-compliance-chip" large />
              </div>
              <div className="flex items-center gap-2">
                {portalStatus?.invited ? (
                  <Btn data-testid="workspace-portal-manage-btn" variant="outline" className="rounded-lg text-xs px-3 py-2" onClick={() => setPortalManage(true)}>
                    <span className={`h-2 w-2 rounded-full ${portalStatus.status === "disabled" ? "bg-[#C62828]" : "bg-[#2E7D32]"}`} />
                    {portalStatus.status === "disabled" ? "Portal Deactivated" : "Client Portal Active"}
                  </Btn>
                ) : (
                  <Btn data-testid="workspace-portal-invite-btn" variant="outline" className="rounded-lg text-xs px-3 py-2"
                    onClick={() => invitePortal(client)} disabled={inviting}>
                    <HeartHandshake size={13} /> {inviting ? "Inviting…" : "Invite to Portal"}
                  </Btn>
                )}
                <Btn data-testid="open-client-console-btn" variant="outline" className="rounded-lg text-xs px-3 py-2"
                  onClick={() => window.open(`/clients/${client.id}/console`, "_blank")}>
                  <SquareArrowOutUpRight size={13} /> Open Console
                </Btn>
              </div>
            </div>
            <AllergyBanner clientId={client.id} />
            <div className="mb-2 flex items-center justify-end">
              <div className="inline-flex items-center gap-0.5 rounded-full border border-[#E5DFD3] bg-[#FDFCF8] p-0.5" role="group" aria-label="Tab density">
                <button type="button" data-testid="workspace-tabmode-compact"
                  onClick={() => tabMode !== "compact" && toggleTabMode()}
                  aria-pressed={tabMode === "compact"}
                  className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors ${tabMode === "compact" ? "bg-[#4E342E] text-white" : "text-[#7B6157] hover:text-[#4E342E]"}`}>
                  Compact
                </button>
                <button type="button" data-testid="workspace-tabmode-expand"
                  onClick={() => tabMode !== "expand" && toggleTabMode()}
                  aria-pressed={tabMode === "expand"}
                  className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors ${tabMode === "expand" ? "bg-[#4E342E] text-white" : "text-[#7B6157] hover:text-[#4E342E]"}`}>
                  Expand
                </button>
              </div>
            </div>
            <TabStrip tabs={INFO_TABS} groups={INFO_TAB_GROUPS} active={infoTab} onChange={setInfoTab} testPrefix="info-tab" mode={tabMode} />
            <Card className="p-6 mb-5" data-testid="client-info-panel">
              <InfoPanel tab={infoTab} client={client} intake={intake} authorizations={authorizations} tasks={clientTasks} onOpenMedList={openMasterMedList}
                onUpdated={(u) => setClients((cs) => cs.map((x) => (x.id === u.id ? u : x)))} />
            </Card>

            {(client.alerts || []).length > 0 && (
              <div className="mb-5" data-testid="client-alerts">
                <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">Alerts</h3>
                <div className="space-y-1.5">
                  {client.alerts.map((a, i) => (
                    <div key={i} className={`${ALERT_COLORS[a.color] || "bg-[#3D5AFE]"} rounded-md px-4 py-2 text-sm text-white flex items-center gap-2`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-white shrink-0" /> {a.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <TabStrip tabs={CHART_TABS} active={chartTab} onChange={setChartTab} testPrefix="chart-tab" mode={tabMode} maxVisible={tabMode === "expand" ? 12 : 8} />
            {chartTab === "AIMS" ? <AimsPanel client={client} /> :
             chartTab === "Vitals" ? <VitalsPanel client={client} editable={clinicalEditable} /> :
             chartTab === "Clinical Assessments" ? <AssessmentsChartPanel client={client} /> :
             chartTab === "Treatment Plans" ? <TreatmentPlansChartPanel client={client} /> :
             chartTab === "Progress Notes" ? <ProgressNotesChartPanel client={client} /> :
             chartTab === "Group Notes" ? <GroupNotesPanel client={client} /> :
             chartTab === "Behavior Tracking" ? <BehaviorChartPanel client={client} editable={clinicalEditable} /> :
             chartTab === "Collateral Contacts" ? <CollateralPanel client={client} editable={clinicalEditable} /> :
             chartTab === "Scanned Documents" ? <DocumentsPanel client={client} category="scanned" editable={clinicalEditable} /> :
             chartTab === "Legacy Documents" ? <DocumentsPanel client={client} category="legacy" editable={clinicalEditable} /> :
             chartTab === "ASAM" ? <AsamChartPanel client={client} /> :
             chartTab === "Medical" ? <ClientMedical key={medListKey} client={client} initialSub={medListKey ? "medications" : undefined} /> :
             chartTab === "Intake" ? <IntakeChartPanel client={client} intake={intake} /> : (
            <>
            {(chartTab === "Orders" || chartTab === "Complete Chart") && (
              <ClientFormsPanel client={client} category={chartTab === "Orders" ? "Orders" : ""} />
            )}
            <Card data-testid="chart-panel">
              <div className="flex flex-wrap items-center justify-between gap-3 p-5 pb-3">
                <h3 className="font-heading font-bold text-[#3E2723]">{chartTab === "Complete Chart" ? "All Chart Plans" : `All ${chartTab}`}</h3>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 rounded-lg border border-[#E5DFD3] pl-3 pr-1 h-10">
                    <Filter size={14} className="text-[#7B6157] shrink-0" />
                    <NativeLikeSelect data-testid="chart-signed-filter" value={signedFilter} onChange={(e) => setSignedFilter(e.target.value)}
                      triggerClass="h-8 w-36 border-0 bg-transparent px-1 text-sm text-[#4E342E] focus:ring-0">
                      <option value="">Filter by: All</option><option value="true">Signed</option><option value="false">Unsigned</option>
                    </NativeLikeSelect>
                  </div>
                  {clinicalEditable && <Btn data-testid="add-chart-note-btn" variant="outline" className="rounded-lg" onClick={() => { setNoteForm({ ...NOTE_EMPTY, category: chartTab === "Complete Chart" ? "Progress Notes" : chartTab }); setEditNoteId(null); setNoteModal(true); }}><Plus size={14} /> Add Note</Btn>}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead><tr>{["Status", "Date", "Time", "Note Type", "Provider", "Program", ""].map((h) => (
                    <th key={h} className="bg-[#FDFCF8] border-y border-[#EFEBE0] text-[#4E342E] font-semibold py-3 px-4 whitespace-nowrap">{h}</th>))}</tr></thead>
                  <tbody>
                    {notes.map((n) => (
                      <tr key={n.id} data-testid={`chart-note-${n.id}`} className="hover:bg-[#FDFCF8]">
                        <td className="border-b border-[#EFEBE0] py-3 px-4">
                          {n.signed ? (
                            <span data-testid={`note-status-${n.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] px-2.5 py-1 text-[10px] font-bold text-[#2E7D32]"><Check size={11} /> Signed</span>
                          ) : (
                            <span data-testid={`note-status-${n.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2.5 py-1 text-[10px] font-bold text-[#8D5A00]">Draft</span>
                          )}
                        </td>
                        <td className="border-b border-[#EFEBE0] py-3 px-4 tabular-nums">{dayjs(n.date).format("MM/DD/YYYY")}</td>
                        <td className="border-b border-[#EFEBE0] py-3 px-4 tabular-nums">{n.time}</td>
                        <td className="border-b border-[#EFEBE0] py-3 px-4">{n.note_type}</td>
                        <td className="border-b border-[#EFEBE0] py-3 px-4">{n.provider}</td>
                        <td className="border-b border-[#EFEBE0] py-3 px-4">{n.program}</td>
                        <td className="border-b border-[#EFEBE0] py-3 px-4">
                          <div className="flex gap-2 justify-end">
                            <button type="button" data-testid={`view-note-${n.id}`} aria-label="View" onClick={() => setViewNote(n)} className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Eye size={13} /></button>
                            {clinicalEditable && !n.signed && <button type="button" data-testid={`sign-note-${n.id}`} aria-label="Sign" title="Sign this note" onClick={() => toggleSign(n)} className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#2E7D32] flex items-center justify-center hover:bg-[#E8F5E9]"><Check size={13} /></button>}
                            {clinicalEditable && <button type="button" data-testid={`edit-note-${n.id}`} aria-label="Edit" onClick={() => { setNoteForm({ date: n.date, time: n.time, note_type: n.note_type, provider: n.provider, program: n.program, category: n.category, custom_notes: n.custom_notes || "" }); setEditNoteId(n.id); setNoteModal(true); }} className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>}
                            {clinicalEditable && <button type="button" data-testid={`delete-note-${n.id}`} aria-label="Delete" onClick={() => setConfirmDelete(n)} className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {notes.length === 0 && <Empty text={`No ${chartTab === "Complete Chart" ? "chart entries" : chartTab.toLowerCase()} for this client`} />}
              </div>
            </Card>
            </>
            )}
          </>
        )}
      </div>

      <SlidePanel open={noteModal} onClose={() => setNoteModal(false)} title={editNoteId ? "Edit Chart Note" : "Add Chart Note"}
        subtitle={client ? `${client.first_name} ${client.last_name} - clinical chart` : ""} testId="note-slide-panel">
        <form onSubmit={saveNote} className="space-y-4">
          <div><FLabel>Note Type</FLabel>
            <FSelect data-testid="note-type-input" value={noteForm.note_type} placeholder="Select a note type"
              placeholderDisabled onChange={(e) => setNoteForm({ ...noteForm, note_type: e.target.value })} required>
              {BUILTIN_NOTE_TYPES.map((t) => <option key={`builtin-${t}`} value={t}>{t}</option>)}
              {customNoteTypes.map((t) => <option key={`custom-${t.id}`} value={t.label}>{t.label}</option>)}
            </FSelect>
            <p className="mt-1 text-[11px] text-[#7B6157]">Selecting a specific type helps route the note to the right reviewer, and the Custom Notes field below stays available for any free text. Admins can add clinic-specific types from Configuration &gt; Note Types.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Date</FLabel><DateField testId="note-date-input" value={noteForm.date} onChange={(v) => setNoteForm({ ...noteForm, date: v })} /></div>
            <div><FLabel>Time</FLabel><FInput type="time" value={noteForm.time} onChange={(e) => setNoteForm({ ...noteForm, time: e.target.value })} /></div>
          </div>
          <div><FLabel>Provider</FLabel><FInput data-testid="note-provider-input" value={noteForm.provider} onChange={(e) => setNoteForm({ ...noteForm, provider: e.target.value })} /></div>
          <div><FLabel>Program</FLabel><SelectWithCustom testId="note-program-input" value={noteForm.program} onChange={(v) => setNoteForm({ ...noteForm, program: v })} options={PROGRAMS} customPlaceholder="Enter program name" /></div>
          <div><FLabel>Category</FLabel>
            <FSelect data-testid="note-category-input" value={noteForm.category} onChange={(e) => setNoteForm({ ...noteForm, category: e.target.value })}>
              {GENERIC_CHART_TABS.filter((t) => t !== "Complete Chart").map((t) => <option key={t}>{t}</option>)}
            </FSelect>
          </div>
          <div><FLabel>Custom Notes</FLabel>
            <textarea data-testid="note-custom-notes-input" rows={4} value={noteForm.custom_notes || ""}
              onChange={(e) => setNoteForm({ ...noteForm, custom_notes: e.target.value })}
              placeholder="Free text notes, observations, or context for this entry."
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723] outline-none focus:border-[#8D6E63]" />
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setNoteModal(false)}>Cancel</Btn>
            <Btn data-testid="note-save-btn" type="submit">{editNoteId ? "Save" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <Modal open={!!viewNote} onClose={() => setViewNote(null)} title="Chart Note">
        {viewNote && (
          <div className="space-y-3 text-sm text-[#4E342E]">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date" value={dayjs(viewNote.date).format("MM/DD/YYYY")} />
              <Field label="Time" value={viewNote.time} />
              <Field label="Note Type" value={viewNote.note_type} />
              <Field label="Category" value={viewNote.category} />
              <Field label="Provider" value={viewNote.provider} />
              <Field label="Program" value={viewNote.program} />
            </div>
            {viewNote.custom_notes && (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#7B6157]">Custom Notes</div>
                <p data-testid="view-note-custom-notes" className="mt-1 whitespace-pre-wrap rounded-lg bg-[#FDFCF8] border border-[#EFEBE0] p-3 text-sm text-[#3E2723]">{viewNote.custom_notes}</p>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-[#F1EEE7] pt-3">
              <div>{viewNote.signed ? <Badge value="completed" /> : <Badge value="open" />} <span className="ml-1 text-xs text-[#84675B]">{viewNote.signed ? "Signed" : "Awaiting signature"}</span></div>
              {clinicalEditable && !viewNote.signed && (
                <Btn data-testid="view-note-sign-btn" className="rounded-lg text-xs px-3 py-1.5"
                  onClick={() => { setSigningNote(viewNote); }}>
                  <Check size={13} /> Sign Note
                </Btn>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={doDelete}
        title="Delete chart note"
        description={confirmDelete ? `Delete "${confirmDelete.note_type}" dated ${dayjs(confirmDelete.date).format("MM/DD/YYYY")} from ${client?.first_name}'s chart?` : ""} />

      <SignaturePromptModal open={!!signingNote} onClose={() => setSigningNote(null)}
        onConfirm={applySignature}
        title="Sign chart note"
        subtitle={signingNote ? `Signing ${signingNote.note_type || "note"} dated ${dayjs(signingNote.date).format("MMM D, YYYY")}. Locking is HIPAA-compliant with a full audit stamp.` : ""} />

      <Modal open={!!inviteResult} onClose={() => setInviteResult(null)} title="Client Portal Invitation">
        {inviteResult && (
          <div className="space-y-4" data-testid="workspace-invite-result">
            <div className="rounded-xl bg-[#E8F5E9] border border-[#C8E6C9] p-3 text-sm text-[#2E7D32] font-semibold">
              Portal access created for {inviteResult.client_name}
            </div>
            <div className="rounded-xl border border-[#EFEBE0] p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-[#7B6157]">Login email</span><span className="font-semibold text-[#3E2723]">{inviteResult.email}</span></div>
              <div className="flex items-center justify-between">
                <span className="text-[#7B6157]">One-time password</span>
                <span className="flex items-center gap-2">
                  <code className="rounded bg-[#FFF8E1] px-2 py-1 font-bold text-[#3E2723]" data-testid="workspace-temp-password">{inviteResult.temp_password}</code>
                  <button onClick={() => { navigator.clipboard.writeText(inviteResult.temp_password); toast.success("Copied"); }} className="p-1.5 rounded-lg hover:bg-[#F5F2EB] text-[#7B6157]"><Copy size={14} /></button>
                </span>
              </div>
            </div>
            <p className="text-xs text-[#84675B]">Share this password securely. On first sign-in the client must set a new password <span className="font-semibold text-[#5D4037]">and enable two-factor authentication</span> before entering the portal.</p>
            <div className="flex justify-between gap-2">
              <Btn variant="outline" data-testid="workspace-invite-email-btn" className="text-xs"
                onClick={() => emailClientInvite(inviteResult.email, inviteResult.temp_password)}>
                <Mail size={13} /> Email to Client
              </Btn>
              <Btn data-testid="workspace-invite-done-btn" onClick={() => setInviteResult(null)}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={portalManage} onClose={() => setPortalManage(false)} title="Client Portal Access">
        {client && portalStatus?.invited && (
          <div className="space-y-4" data-testid="portal-manage-modal">
            <div className="rounded-xl border border-[#EFEBE0] p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-[#7B6157]">Status</span>
                <span className="flex items-center gap-1.5 font-bold capitalize" data-testid="portal-manage-status">
                  <span className={`h-2 w-2 rounded-full ${portalStatus.status === "disabled" ? "bg-[#C62828]" : "bg-[#2E7D32]"}`} />
                  {portalStatus.status === "disabled" ? "Deactivated" : portalStatus.status}
                </span></div>
              <div className="flex items-center justify-between"><span className="text-[#7B6157]">Login email</span><span className="font-semibold text-[#3E2723]">{portalStatus.email}</span></div>
              <div className="flex items-center justify-between"><span className="text-[#7B6157]">Last login</span><span>{portalStatus.last_login ? dayjs(portalStatus.last_login).format("MM/DD/YYYY h:mm A") : "Never"}</span></div>
              <div className="flex items-center justify-between"><span className="text-[#7B6157]">Invited</span><span>{portalStatus.invited_at ? dayjs(portalStatus.invited_at).format("MM/DD/YYYY") : "-"}</span></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button data-testid="portal-manage-email-btn" onClick={() => emailClientInvite(portalStatus.email, null)}
                className="flex flex-col items-center gap-1 rounded-xl border border-[#D7CCC8] bg-[#F5F2EB] px-2 py-2.5 text-xs font-bold text-[#5D4037] hover:bg-[#EDE7DC]">
                <Mail size={15} /> Email Client
              </button>
              <button data-testid="portal-manage-reset-btn" disabled={!isAdmin || inviting} onClick={() => invitePortal(client)}
                title={isAdmin ? "Generate a new one-time password (also resets 2FA)" : "Tenant admin only"}
                className="flex flex-col items-center gap-1 rounded-xl border border-[#FFE0B2] bg-[#FFF3E0] px-2 py-2.5 text-xs font-bold text-[#8D5A00] hover:bg-[#FFE0B2] disabled:opacity-40 disabled:cursor-not-allowed">
                <KeyRound size={15} /> {inviting ? "Resetting…" : "Reset Password"}
              </button>
              {portalStatus.status === "disabled" ? (
                <button data-testid="portal-manage-activate-btn" disabled={!isAdmin || portalBusy} onClick={() => togglePortalAccess(client, false)}
                  title={isAdmin ? "" : "Tenant admin only"}
                  className="flex flex-col items-center gap-1 rounded-xl border border-[#C8E6C9] bg-[#E8F5E9] px-2 py-2.5 text-xs font-bold text-[#2E7D32] hover:bg-[#C8E6C9] disabled:opacity-40 disabled:cursor-not-allowed">
                  <ShieldCheck size={15} /> Reactivate Access
                </button>
              ) : (
                <button data-testid="portal-manage-deactivate-btn" disabled={!isAdmin || portalBusy} onClick={() => togglePortalAccess(client, true)}
                  title={isAdmin ? "" : "Tenant admin only"}
                  className="flex flex-col items-center gap-1 rounded-xl border border-[#FFCDD2] bg-[#FFEBEE] px-2 py-2.5 text-xs font-bold text-[#C62828] hover:bg-[#FFCDD2] disabled:opacity-40 disabled:cursor-not-allowed">
                  <ShieldOff size={15} /> Deactivate Access
                </button>
              )}
            </div>
            {!isAdmin && <p className="text-[11px] text-[#84675B]">Password reset and access activation/deactivation are restricted to tenant admins.</p>}
            <p className="rounded-lg bg-[#F5F2EB] px-3 py-2 text-[10px] leading-relaxed text-[#7B6157]">
              All portal access changes are audit-logged and enforced server-side - HIPAA §164.312(a)(1)/(b) access control & audit, NIST SP 800-53 AC-2/AU-2, CMMC L2 AC.L2-3.1.1.
            </p>
            <div className="flex justify-end"><Btn onClick={() => setPortalManage(false)}>Close</Btn></div>
          </div>
        )}
      </Modal>
      <ClientQuickDrawer client={quickClient} open={!!quickClient} onClose={() => setQuickClient(null)}
        onUpdated={() => api.get("/clients", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => setClients(r.data))} />
    </div>
  );
}
